import axios from 'axios';
import { auth } from './firebase';
import { OptionalQuestions } from './types/game';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Token management with expiration
interface TokenData {
  token: string;
  expiresAt: number; // timestamp in milliseconds
}

let externalTokenData: TokenData | null = null;
let authListeners: Array<()=>void> = [];

// Custom event for requesting token refresh
const requestTokenRefresh = () => {
  window.dispatchEvent(new CustomEvent('tokenRefreshRequired'));
};

// Event listener for iOS WebView token
window.addEventListener('nativeTokenReceived', ((event: CustomEvent) => {
  const token = event.detail;
  // Decode JWT to get expiration if it exists
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    externalTokenData = {
      token,
      // exp is in seconds, convert to milliseconds and subtract 30 seconds for safety margin
      expiresAt: (payload.exp * 1000) - 30000
    };
  } catch (e) {
    // If token doesn't have expiration, set a default 1 hour expiration
    externalTokenData = {
      token,
      expiresAt: Date.now() + 3600000 // 1 hour from now
    };
  }
  notifySubscribers();
}) as EventListener);

// Event listener for iframe token
window.addEventListener('message', (event) => {
  // Verify origin for security
  if (event.origin !== window.location.origin) {
    const allowedOrigins = process.env.REACT_APP_ALLOWED_ORIGINS?.split(',') || [];
    if (!allowedOrigins.includes(event.origin)) {
      return;
    }
  }

  if (event.data?.type === 'TOKEN_TRANSFER' && event.data?.token) {
    const token = event.data.token;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      externalTokenData = {
        token,
        expiresAt: (payload.exp * 1000) - 30000
      };
    } catch (e) {
      externalTokenData = {
        token,
        expiresAt: Date.now() + 3600000
      };
    }
  }
  notifySubscribers();
});

const hasToken = () => {
  return !!externalTokenData
}

const subscribeToAuth = (listener: () => void) => {
  authListeners.push(listener);

  return () => {
    authListeners = authListeners.filter((l) => l !== listener);
  };
};

const notifySubscribers = () => {
  for (const listener of authListeners) {
    listener();
  }
}


const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Request interceptor with detailed logging
api.interceptors.request.use(async (config) => {
  try {
    // Try Firebase auth first
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }

    // Check external token and its expiration
    if (externalTokenData) {
      if (Date.now() >= externalTokenData.expiresAt) {
        // Token is expired or about to expire
        requestTokenRefresh();
        return Promise.reject(new Error('Token expired'));
      }
      
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${externalTokenData.token}`;
      return config;
    }

    return config;
  } catch (error) {
    console.error("Authentication error:", error);
    return Promise.reject(error);
  }
}, (error) => {
  console.error("Request interceptor error:", error);
  return Promise.reject(error);
});

// Clear external token on 401 responses
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      externalTokenData = null;
      requestTokenRefresh();
      auth.signOut();
    }
    return Promise.reject(error);
  }
);

// Export token management functions
export const setExternalToken = (token: string) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    externalTokenData = {
      token,
      expiresAt: (payload.exp * 1000) - 30000
    };
  } catch (e) {
    externalTokenData = {
      token,
      expiresAt: Date.now() + 3600000
    };
  }
};

export const clearExternalToken = () => {
  externalTokenData = null;
};

// Game API endpoints
const gameApi = {
  startGame: (category?: string) => api.post('/api/game/start', category ? { category } : undefined),
  getGameState: () => api.get('/api/game/state'),
  submitAnswer: (answer: number) => api.post('/api/game/answer', { answer }),
  getNextQuestion: () => api.get('/api/game/next_question'),
};

// User API endpoints
const userApi = {
  getStats: () => api.get('/api/users/stats'),
  register: (username: string, optionalQuestions?: OptionalQuestions, consent?: {consented: boolean, privacy_notice_accepted: boolean}) => 
    api.post('/api/users/register', { username, optional_questions: optionalQuestions, consent }),
  getProfile: () => api.get('/api/users/me'),
  updateCategory: (category: string) => api.post('/api/users/category', { category }),
  getAvailableCategories: () => api.get('/api/users/available-categories'),
  submitConsent: (consented: boolean, privacy_notice_accepted: boolean) => api.post('/api/users/consent', { consented, privacy_notice_accepted }),
  updateOptionalQuestions: (optionalQuestions: OptionalQuestions) => 
    api.post('/api/users/optional-questions', { optional_questions: optionalQuestions }),
};

// Leaderboard API endpoints
const leaderboardApi = {
  getLeaderboard: (category: string) => api.get('/api/leaderboard', { params: { category } }),
  getLeaderboardCategories: () => api.get('/api/leaderboard/categories'),
  getUserRank: (username: string) => api.get(`/api/leaderboard/rank/${username}`)
};

// Achievement API endpoints
const achievementsApi = {
  getAllAchievements: () => api.get('/api/achievements')
};

const prizeApi = {
  getAllPrizes: () => api.get('/api/prizes'),
  getUserPrizes: () => api.get('/api/users/prizes'),
};

export default api;
export { gameApi, userApi, leaderboardApi, achievementsApi, prizeApi, hasToken, subscribeToAuth }; 